export interface NormalizedApiErrorItem {
  field?: string
  code?: string
  message: string
}

export interface NormalizedApiError {
  code?: string
  message: string
  errors?: NormalizedApiErrorItem[]
  raw?: unknown
  voyager?: unknown
}

function normalizeErrorItems(items: unknown): NormalizedApiErrorItem[] | undefined {
  if (!Array.isArray(items)) return undefined

  return items.map((item) => {
    const error = (item || {}) as Record<string, any>
    return {
      field: error.field || undefined,
      code: error.businessSystemCode || error.code || undefined,
      message: String(error.message || 'Invalid'),
    }
  })
}

/**
 * Attempts to extract a JSON object embedded in a string, e.g.
 * "Voyager error: {\"code\":\"400\",...}"
 */
function parseEmbeddedJsonFromStatus(status: string): any | null {
  if (typeof status !== 'string') return null
  const firstBrace = status.indexOf('{')
  if (firstBrace < 0) return null
  const jsonStr = status.slice(firstBrace)
  try {
    return JSON.parse(jsonStr)
  } catch (_) {
    return null
  }
}

/**
 * Normalize odd API error shapes into a consistent structure.
 * Specifically supports responses where useful details are buried in
 * a string field (e.g. `status`) containing JSON from Voyager.
 */
export function normalizeApiError(input: unknown): NormalizedApiError {
  // Already normalized
  if (typeof input === 'object' && input != null) {
    const obj = input as Record<string, any>
    const normalizedErrors = normalizeErrorItems(obj.errors) || normalizeErrorItems(obj.details)

    // Voyager pattern: details inside status string
    if (typeof obj.status === 'string') {
      const embedded = parseEmbeddedJsonFromStatus(obj.status)
      if (embedded && typeof embedded === 'object') {
        const { code } = embedded
        const errors = normalizeErrorItems(embedded.errors)
        const message = errors?.length ? errors.map((e) => e.message).join('; ') : String(embedded.message || obj.error || 'Unexpected error')
        return { code, message, errors, raw: input, voyager: embedded }
      }
    }

    // Standard MkErrorResponse shape from mkback
    if (
      typeof obj.errorCode === 'string' ||
      typeof obj.errorMessage === 'string' ||
      (Array.isArray(obj.details) && obj.details.length > 0)
    ) {
      return {
        code: typeof obj.errorCode === 'string' ? obj.errorCode : obj.code,
        message: String(obj.errorMessage || obj.message || obj.error || 'Unexpected error'),
        errors: normalizedErrors,
        raw: input,
      }
    }

    // Common shape with message/error
    if (typeof (obj as any).message === 'string' || typeof (obj as any).error === 'string') {
      return {
        code: (obj as any).code || (obj as any).errorCode,
        message: String((obj as any).message || (obj as any).error),
        errors: normalizedErrors,
        raw: input,
      }
    }
  }

  // As a last resort, stringify input
  try {
    if (typeof input === 'string') return { message: input }
    return { message: JSON.stringify(input) }
  } catch {
    return { message: 'Unknown error' }
  }
}
